import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Autocomplete, Table, TableBody, TableCell,
  TableHead, TableRow, TableContainer, Chip, Alert, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Tooltip, Grid, MenuItem, LinearProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PaymentIcon from '@mui/icons-material/Payment';
import CancelIcon from '@mui/icons-material/Cancel';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import axios from 'axios';

const fmt = n => `S/ ${Number(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}`;
const fmtDate = v => v ? new Date(v + 'T12:00:00').toLocaleDateString('es-PE') : '—';

const EMPTY_FORM = {
  empresa_id: '', trabajador_id: '', descripcion: 'Préstamo personal',
  monto_total: '', cuota_mensual: '', cuotas_total: '', fecha_inicio: '', observaciones: ''
};

export default function Prestamos() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [trabajadores, setTrabajadores] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('ACTIVO');

  // Nuevo préstamo
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [savingNuevo, setSavingNuevo] = useState(false);

  // Pago manual
  const [pagoOpen, setPagoOpen] = useState(false);
  const [pagoRow, setPagoRow] = useState(null);
  const [pagoMonto, setPagoMonto] = useState('');
  const [pagoObs, setPagoObs] = useState('');
  const [savingPago, setSavingPago] = useState(false);

  useEffect(() => {
    axios.get('/api/rrhh/empresas').then(r => {
      setEmpresas(Array.isArray(r.data) ? r.data : []);
      if (r.data?.length === 1) setEmpresaId(r.data[0].empresa_id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) return;
    axios.get(`/api/rrhh/trabajadores?empresa_id=${empresaId}&estado=ACTIVO`).then(r => {
      setTrabajadores(Array.isArray(r.data) ? r.data : []);
    }).catch(() => {});
  }, [empresaId]);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`/api/rrhh/prestamos?empresa_id=${empresaId}${filtroEstado !== 'TODOS' ? `&estado=${filtroEstado}` : ''}`);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar'); }
    finally { setLoading(false); }
  }, [empresaId, filtroEstado]);

  useEffect(() => { cargar(); }, [cargar]);

  const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const calcCuotas = (monto, cuota) => {
    if (monto && cuota && Number(cuota) > 0) {
      sf('cuotas_total', String(Math.ceil(Number(monto) / Number(cuota))));
    }
  };

  const handleGuardar = async () => {
    if (!form.trabajador_id || !form.monto_total || !form.cuota_mensual || !form.fecha_inicio)
      return setError('Complete todos los campos obligatorios');
    setSavingNuevo(true);
    setError('');
    try {
      await axios.post('/api/rrhh/prestamos', { ...form, empresa_id: empresaId });
      setNuevoOpen(false);
      setForm(EMPTY_FORM);
      setSuccess('Préstamo registrado correctamente');
      cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al guardar'); }
    finally { setSavingNuevo(false); }
  };

  const handleCancelar = async (id) => {
    if (!window.confirm('¿Cancelar este préstamo?')) return;
    try {
      await axios.put(`/api/rrhh/prestamos/${id}/cancelar`);
      setSuccess('Préstamo cancelado');
      cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
  };

  const handlePago = async () => {
    if (!pagoMonto || Number(pagoMonto) <= 0) return setError('Ingrese un monto válido');
    setSavingPago(true);
    try {
      const r = await axios.post(`/api/rrhh/prestamos/${pagoRow.id}/pago`, { monto: pagoMonto, observaciones: pagoObs });
      setPagoOpen(false);
      setSuccess(`Pago registrado. Nuevo saldo: ${fmt(r.data.nuevo_saldo)}`);
      cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al registrar pago'); }
    finally { setSavingPago(false); }
  };

  const totalSaldo = rows.filter(r => r.estado === 'ACTIVO').reduce((a, r) => a + Number(r.saldo || 0), 0);
  const totalMonto = rows.filter(r => r.estado === 'ACTIVO').reduce((a, r) => a + Number(r.monto_total || 0), 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '92%' }, maxWidth: 1200 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <AccountBalanceWalletIcon sx={{ color: '#7b1fa2', fontSize: 32 }} />
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#222' }}>Préstamos al Personal</Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete
            options={empresas} getOptionLabel={o => o.razon_social || ''}
            value={empresas.find(e => e.empresa_id === empresaId) || null}
            onChange={(_, v) => setEmpresaId(v?.empresa_id || '')}
            isOptionEqualToValue={(o, v) => o.empresa_id === v.empresa_id}
            renderInput={p => <TextField {...p} label="Empresa" size="small" sx={{ minWidth: '16em' }} />}
            sx={{ minWidth: '16em' }}
          />
          <TextField select label="Estado" size="small" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} sx={{ minWidth: '12em' }}>
            <MenuItem value="TODOS">Todos</MenuItem>
            <MenuItem value="ACTIVO">Activos</MenuItem>
            <MenuItem value="CANCELADO">Cancelados</MenuItem>
          </TextField>
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm(EMPTY_FORM); setNuevoOpen(true); }} disabled={!empresaId} sx={{ bgcolor: '#7b1fa2' }}>
            Nuevo Préstamo
          </Button>
        </Box>

        {empresaId && rows.filter(r => r.estado === 'ACTIVO').length > 0 && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Paper sx={{ p: 2, borderRadius: 3, flex: 1, minWidth: 180, textAlign: 'center', bgcolor: '#f3e5f5' }}>
              <Typography variant="caption" color="text.secondary">Préstamos activos</Typography>
              <Typography variant="h5" fontWeight="bold" color="#7b1fa2">
                {rows.filter(r => r.estado === 'ACTIVO').length}
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, borderRadius: 3, flex: 1, minWidth: 180, textAlign: 'center', bgcolor: '#fff3e0' }}>
              <Typography variant="caption" color="text.secondary">Saldo total pendiente</Typography>
              <Typography variant="h5" fontWeight="bold" color="#e65100">{fmt(totalSaldo)}</Typography>
            </Paper>
            <Paper sx={{ p: 2, borderRadius: 3, flex: 1, minWidth: 180, textAlign: 'center', bgcolor: '#e8f5e9' }}>
              <Typography variant="caption" color="text.secondary">Monto total otorgado</Typography>
              <Typography variant="h5" fontWeight="bold" color="#2e7d32">{fmt(totalMonto)}</Typography>
            </Paper>
          </Box>
        )}

        <Paper sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Trabajador</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Monto Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Saldo</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Cuota Mens.</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Cuotas</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Avance</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Inicio</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {empresaId ? 'No hay préstamos registrados' : 'Seleccione una empresa'}
                    </TableCell></TableRow>
                  ) : rows.map(r => {
                    const pct = r.cuotas_total > 0 ? Math.round((r.cuotas_pagadas / r.cuotas_total) * 100) : 0;
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell><strong>{r.trabajador_nombre}</strong></TableCell>
                        <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.descripcion}</TableCell>
                        <TableCell align="right">{fmt(r.monto_total)}</TableCell>
                        <TableCell align="right" sx={{ color: r.estado === 'ACTIVO' ? '#e65100' : 'inherit', fontWeight: r.estado === 'ACTIVO' ? 'bold' : 'normal' }}>
                          {fmt(r.saldo)}
                        </TableCell>
                        <TableCell align="right">{fmt(r.cuota_mensual)}</TableCell>
                        <TableCell align="center">{r.cuotas_pagadas}/{r.cuotas_total}</TableCell>
                        <TableCell sx={{ minWidth: 100 }}>
                          <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3, bgcolor: '#f5f5f5', '& .MuiLinearProgress-bar': { bgcolor: pct >= 100 ? '#2e7d32' : '#7b1fa2' } }} />
                          <Typography variant="caption" color="text.secondary">{pct}%</Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.fecha_inicio)}</TableCell>
                        <TableCell align="center">
                          <Chip label={r.estado} size="small" color={r.estado === 'ACTIVO' ? 'warning' : 'success'} />
                        </TableCell>
                        <TableCell align="center">
                          {r.estado === 'ACTIVO' && (<>
                            <Tooltip title="Registrar pago manual">
                              <IconButton size="small" sx={{ color: '#7b1fa2' }} onClick={() => { setPagoRow(r); setPagoMonto(String(r.cuota_mensual)); setPagoObs(''); setPagoOpen(true); }}>
                                <PaymentIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancelar préstamo">
                              <IconButton size="small" color="error" onClick={() => handleCancelar(r.id)}>
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Diálogo nuevo préstamo */}
      <Dialog open={nuevoOpen} onClose={() => setNuevoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight="bold">Nuevo Préstamo al Personal</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={trabajadores}
                getOptionLabel={o => `${o.apellido_paterno} ${o.apellido_materno}, ${o.nombres}` || ''}
                value={trabajadores.find(t => t.id === form.trabajador_id) || null}
                onChange={(_, v) => sf('trabajador_id', v?.id || '')}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={p => <TextField {...p} label="Trabajador *" size="small" sx={{ minWidth: '16em' }} />}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descripción" size="small" value={form.descripcion} onChange={e => sf('descripcion', e.target.value)} sx={{ minWidth: '16em' }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Monto Total (S/) *" size="small" type="number" value={form.monto_total}
                onChange={e => { sf('monto_total', e.target.value); calcCuotas(e.target.value, form.cuota_mensual); }}
                sx={{ minWidth: '10em' }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Cuota Mensual (S/) *" size="small" type="number" value={form.cuota_mensual}
                onChange={e => { sf('cuota_mensual', e.target.value); calcCuotas(form.monto_total, e.target.value); }}
                sx={{ minWidth: '10em' }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Cuotas Totales" size="small" type="number" value={form.cuotas_total}
                onChange={e => sf('cuotas_total', e.target.value)} sx={{ minWidth: '10em' }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Fecha de Inicio *" size="small" type="date" value={form.fecha_inicio}
                onChange={e => sf('fecha_inicio', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: '10em' }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Observaciones" size="small" multiline rows={2} value={form.observaciones}
                onChange={e => sf('observaciones', e.target.value)} sx={{ minWidth: '16em' }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNuevoOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleGuardar} disabled={savingNuevo} sx={{ bgcolor: '#7b1fa2' }}>
            {savingNuevo ? <CircularProgress size={20} /> : 'Registrar Préstamo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo pago manual */}
      <Dialog open={pagoOpen} onClose={() => setPagoOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight="bold">Registrar Pago de Préstamo</DialogTitle>
        <DialogContent>
          {pagoRow && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f3e5f5', borderRadius: 2 }}>
              <Typography variant="body2"><strong>{pagoRow.trabajador_nombre}</strong></Typography>
              <Typography variant="body2" color="text.secondary">{pagoRow.descripcion}</Typography>
              <Typography variant="body2">Saldo actual: <strong style={{ color: '#e65100' }}>{fmt(pagoRow.saldo)}</strong></Typography>
            </Box>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Monto del pago (S/)" size="small" type="number" value={pagoMonto}
                onChange={e => setPagoMonto(e.target.value)} sx={{ minWidth: '16em' }}
                inputProps={{ max: pagoRow?.saldo }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Observación" size="small" value={pagoObs} onChange={e => setPagoObs(e.target.value)} sx={{ minWidth: '16em' }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPagoOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handlePago} disabled={savingPago} sx={{ bgcolor: '#7b1fa2' }}>
            {savingPago ? <CircularProgress size={20} /> : 'Registrar Pago'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
